/**
 * ProjectionChart — ported from the "OnFlow Design System" project
 * (v2/components/track/ProjectionChart.jsx).
 *
 * Weight trend against the line you would need to hit the goal on time. The
 * shaded wedge between the two lines IS the story: if the dashed required
 * line pulls away from the solid actual line, the goal date is slipping.
 */
import type { CSSProperties } from 'react';

import type { TrackState } from './FlowStatus.js';

export interface ProjectionPoint {
  /** Value on the y axis (kg). */
  v: number;
  /** Optional x index — defaults to the point's position in the array. */
  i?: number;
}

interface ProjectionChartProps {
  points: ReadonlyArray<ProjectionPoint>;
  required: ReadonlyArray<ProjectionPoint>;
  goalValue?: number;
  unit?: string;
  state?: Exclude<TrackState, 'neutral'>;
  height?: number;
  /** One-sentence read of the wedge, rendered under the legend. */
  note?: string;
  style?: CSSProperties;
}

const ACCENT: Record<Exclude<TrackState, 'neutral'>, string> = {
  'on-track': 'var(--on-track)',
  drifting: 'var(--drifting)',
  'off-track': 'var(--off-track)',
};

export function ProjectionChart({
  points,
  required,
  goalValue,
  unit = 'kg',
  state = 'on-track',
  height = 168,
  note,
  style,
}: ProjectionChartProps) {
  const accent = ACCENT[state];
  const all = [...points.map((p) => p.v), ...required.map((p) => p.v), goalValue].filter(
    (n): n is number => typeof n === 'number',
  );
  if (all.length === 0) return null;

  const min = Math.min(...all) - 0.4;
  const max = Math.max(...all) + 0.4;
  const W = 320;
  const H = height;
  const padL = 30;
  const padB = 18;
  const padT = 8;
  const spanX = Math.max(1, (required.length || points.length) - 1);
  const x = (i: number) => padL + (i / spanX) * (W - padL - 6);
  const y = (v: number) => padT + (1 - (v - min) / Math.max(0.001, max - min)) * (H - padT - padB);
  const line = (arr: ReadonlyArray<ProjectionPoint>) =>
    arr.map((p, i) => `${i ? 'L' : 'M'}${x(p.i ?? i)} ${y(p.v)}`).join(' ');

  // The wedge closes the actual line back along the required line, so the
  // filled area is exactly the gap between "where you are" and "where the
  // plan needs you".
  const lastIdx = points.length - 1;
  const areaPath =
    points.length > 1 && required.length > 1
      ? `${line(points)} L ${x(points[lastIdx]!.i ?? lastIdx)} ${y(
          required[points[lastIdx]!.i ?? lastIdx]?.v ?? points[lastIdx]!.v,
        )} ${required
          .slice(0, points.length)
          .reverse()
          .map((p, k) => `L${x(lastIdx - k)} ${y(p.v)}`)
          .join(' ')} Z`
      : null;
  const last = points[lastIdx];

  return (
    <div style={style}>
      <svg
        viewBox={`0 0 ${W} ${H}`}
        width="100%"
        height={H}
        style={{ overflow: 'visible', display: 'block' }}
        role="img"
        aria-label="Weight trend against the line needed to hit your goal"
      >
        {[max, (max + min) / 2, min].map((v, k) => (
          <g key={k}>
            <line x1={padL} x2={W - 6} y1={y(v)} y2={y(v)} stroke="var(--edge)" strokeWidth="1" />
            <text
              x={0}
              y={y(v) + 3}
              fill="var(--ink-3)"
              style={{ fontFamily: 'var(--font-mono)', fontSize: 9 }}
            >
              {v.toFixed(1)}
            </text>
          </g>
        ))}
        {typeof goalValue === 'number' && (
          <g>
            <line
              x1={padL}
              x2={W - 6}
              y1={y(goalValue)}
              y2={y(goalValue)}
              stroke={accent}
              strokeWidth="1"
              strokeDasharray="1 3"
              opacity="0.55"
            />
            <text
              x={W - 6}
              y={y(goalValue) - 5}
              textAnchor="end"
              fill={accent}
              style={{ fontFamily: 'var(--font-mono)', fontSize: 9, letterSpacing: '0.06em' }}
            >
              GOAL {goalValue}
              {unit}
            </text>
          </g>
        )}
        {areaPath && <path d={areaPath} fill={accent} opacity="0.1" />}
        {required.length > 1 && (
          <path
            d={line(required)}
            fill="none"
            stroke="var(--ink-3)"
            strokeWidth="1.5"
            strokeDasharray="4 4"
            strokeLinecap="round"
          />
        )}
        {points.length > 1 && (
          <path
            d={line(points)}
            fill="none"
            stroke={accent}
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            style={{
              filter: `drop-shadow(0 0 6px color-mix(in srgb, ${accent} 45%, transparent))`,
            }}
          />
        )}
        {points.map((p, i) => (
          <circle
            key={i}
            cx={x(p.i ?? i)}
            cy={y(p.v)}
            r={i === lastIdx ? 4 : 2.5}
            fill={i === lastIdx ? accent : 'var(--paper-0)'}
            stroke={accent}
            strokeWidth="1.5"
          />
        ))}
        {last && (
          <text
            x={x(last.i ?? lastIdx)}
            y={y(last.v) - 12}
            textAnchor="middle"
            fill="var(--ink-1)"
            style={{ fontFamily: 'var(--font-display)', fontSize: 12, fontWeight: 700 }}
          >
            {last.v}
            {unit}
          </text>
        )}
      </svg>
      <div style={{ display: 'flex', gap: 16, marginTop: 10, flexWrap: 'wrap' }}>
        <LegendItem
          swatch={
            <span style={{ width: 14, height: 2.5, borderRadius: 999, background: accent }} />
          }
        >
          Actual
        </LegendItem>
        <LegendItem
          swatch={
            <span
              style={{ width: 14, height: 0, borderTop: '2px dashed var(--ink-3)', flexShrink: 0 }}
            />
          }
        >
          Needed to hit {goalValue}
          {unit}
        </LegendItem>
      </div>
      {note && (
        <p style={{ margin: '10px 0 0', fontSize: 13, lineHeight: 1.5, color: 'var(--ink-2)' }}>
          {note}
        </p>
      )}
    </div>
  );
}

function LegendItem({ swatch, children }: { swatch: React.ReactNode; children: React.ReactNode }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
      {swatch}
      <span
        style={{
          fontFamily: 'var(--font-mono)',
          fontSize: 10,
          letterSpacing: '0.06em',
          textTransform: 'uppercase',
          color: 'var(--ink-3)',
        }}
      >
        {children}
      </span>
    </span>
  );
}
